import { createClient } from '@sanity/client'
import { QueryClient } from '@tanstack/react-query'
import imageUrlBuilder from '@sanity/image-url'

// Create a new QueryClient instance with better error handling
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always fetch fresh data during development
      gcTime: 1000 * 60 * 30, // Cache is kept for 30 minutes
      refetchOnWindowFocus: false, // Disable to prevent excessive requests
      refetchOnReconnect: true, // Enable refetching on reconnect
      retry: 5, // Increase retry attempts
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000), // Exponential backoff
    },
  },
});

// Essential: clear cache on startup to prevent stale data
queryClient.clear();

// Get Sanity configuration from environment variables with fallbacks
const projectId = import.meta.env.VITE_SANITY_PROJECT_ID || '6z58iymr';
const dataset = import.meta.env.VITE_SANITY_DATASET || 'production';

// Log Sanity configuration for debugging
console.log('Sanity Configuration:', { 
  projectId, 
  dataset, 
  apiUrl: `https://${projectId}.api.sanity.io/v2021-10-21/data/query/${dataset}`
});

// Mock data has been moved to a dedicated file: mockData.ts
// This allows for a more comprehensive and organized mock data system

// Create Sanity client with improved configuration
export const client = createClient({
  projectId,
  dataset,
  apiVersion: '2021-10-21', // Use stable API version
  useCdn: false, // Disable CDN to ensure fresh content
  perspective: 'published',
  token: import.meta.env.VITE_SANITY_TOKEN, // Include token if available
  // Note: using 'as any' to add non-standard options that are supported by the library
  // but not included in the TypeScript types
} as any)

// Create image URL builder from the client
const builder = imageUrlBuilder(client)
export const urlFor = (source: any) => {
  return builder.image(source)
}

// Helper function to create cache keys
export const createCacheKey = (type: string, params?: object) => {
  return [`sanity-${type}`, params]
}
